import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import DashboardPage from './pages/DashboardPage';
import SimulationPage from './pages/SimulationPage';
import OnboardingPage from './pages/OnboardingPage';
import EntrepreneurTrainingPage from './pages/EntrepreneurTrainingPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import EnterpriseDashboardPage from './pages/EnterpriseDashboardPage';
import PathToSuccessPage from './pages/PathToSuccessPage';
import ProfilePage from './pages/ProfilePage';
import FeedbackButton from './components/FeedbackButton';
import TalentDashboard from './pages/employer/TalentDashboard';
import DCVEELandingPage from './pages/dcvee/LandingPage';
import DCVEELoginPage from './pages/dcvee/LoginPage';
import DCVEEDashboardPage from './pages/dcvee/DashboardPage';
import DCVEEOnboardingPage from './pages/dcvee/OnboardingPage';
import VirtualWorkspacePage from './pages/dcvee/VirtualWorkspacePage';
import CRMPage from './pages/dcvee/systems/CRMPage';
import ERPPage from './pages/dcvee/systems/ERPPage';
import ProjectManagementPage from './pages/dcvee/systems/ProjectManagementPage';
import SupportPage from './pages/dcvee/systems/SupportPage';
import TeamCollaborationPage from './pages/dcvee/systems/TeamCollaborationPage';
import HRPortalPage from './pages/dcvee/systems/HRPortalPage';
import AITaskAssignmentPage from './pages/dcvee/systems/AITaskAssignmentPage';
import DecisionSimulationPage from './pages/dcvee/systems/DecisionSimulationPage';
import BrowserPage from './pages/dcvee/systems/BrowserPage';
import TrainingDashboardPage from './pages/TrainingDashboardPage';
import TrainingPortalPage from './pages/TrainingPortalPage';
import { AzureAdminModule1, AzureFundamentals, AzureAdministrator } from './pages/training';
import { useAuth } from './contexts/AuthContext';
import FloatingChatbot from './components/FloatingChatbot';

function App() {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();
  
  // Check if the current route is a protected route
  const isProtectedRoute = 
    location.pathname === '/dashboard' || 
    location.pathname.startsWith('/simulacion/') || 
    location.pathname === '/entrepreneur-training' ||
    location.pathname === '/onboarding' ||
    location.pathname === '/admin' ||
    location.pathname === '/employer/marketplace' ||
    location.pathname === '/enterprise' ||
    location.pathname === '/ruta-al-exito' ||
    location.pathname === '/perfil' ||
    location.pathname.startsWith('/dcvee/dashboard') ||
    location.pathname.startsWith('/dcvee/onboarding') ||
    location.pathname.startsWith('/dcvee/systems/') ||
    location.pathname === '/training' ||
    location.pathname.startsWith('/training/');
  
  // Check if we're on the homepage
  const isHomePage = location.pathname === '/';

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <>
      {!isProtectedRoute && <Navbar />}
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={user ? <Navigate to={isAdmin ? "/admin" : "/dashboard"} replace /> : <LoginPage />} />
          <Route path="/employer/login" element={user ? <Navigate to="/employer/marketplace" replace /> : <LoginPage />} />
          <Route path="/dcvee" element={<DCVEELandingPage />} />
          <Route path="/dcvee/login" element={user ? <Navigate to="/dcvee/dashboard" replace /> : <DCVEELoginPage />} />
          <Route path="/dcvee/dashboard" element={user ? <DCVEEDashboardPage /> : <Navigate to="/dcvee/login" replace />} />
          <Route path="/dcvee/onboarding" element={user ? <DCVEEOnboardingPage /> : <Navigate to="/dcvee/login" replace />} />
          <Route path="/dcvee/workspace" element={user ? <VirtualWorkspacePage /> : <Navigate to="/dcvee/login" replace />} />
          
          {/* DCVEE System Routes */}
          <Route path="/dcvee/systems/crm" element={user ? <CRMPage /> : <Navigate to="/dcvee/login" replace />} />
          <Route path="/dcvee/systems/erp" element={user ? <ERPPage /> : <Navigate to="/dcvee/login" replace />} />
          <Route path="/dcvee/systems/project-management" element={user ? <ProjectManagementPage /> : <Navigate to="/dcvee/login" replace />} />
          <Route path="/dcvee/systems/support" element={user ? <SupportPage /> : <Navigate to="/dcvee/login" replace />} />
          <Route path="/dcvee/systems/collaboration" element={user ? <TeamCollaborationPage /> : <Navigate to="/dcvee/login" replace />} />
          <Route path="/dcvee/systems/hr" element={user ? <HRPortalPage /> : <Navigate to="/dcvee/login" replace />} />
          <Route path="/dcvee/systems/ai-tasks" element={user ? <AITaskAssignmentPage /> : <Navigate to="/dcvee/login" replace />} />
          <Route path="/dcvee/systems/decisions" element={user ? <DecisionSimulationPage /> : <Navigate to="/dcvee/login" replace />} />
          <Route path="/dcvee/systems/browser" element={user ? <BrowserPage /> : <Navigate to="/dcvee/login" replace />} />
          
          <Route path="/registro" element={user ? <Navigate to="/onboarding" replace /> : <RegisterPage />} />
          <Route path="/precios" element={<PricingPage />} />
          <Route path="/nosotros" element={<AboutPage />} />
          <Route path="/formacion" element={<TrainingPortalPage />} />
          <Route path="/dashboard" element={user ? <DashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/onboarding" element={user ? <OnboardingPage /> : <Navigate to="/login" replace />} />
          <Route path="/simulacion/:id" element={user ? <SimulationPage /> : <Navigate to="/login" replace />} />
          <Route path="/entrepreneur-training" element={user ? <EntrepreneurTrainingPage /> : <Navigate to="/login" replace />} />
          <Route path="/admin" element={isAdmin ? <AdminDashboardPage /> : <Navigate to="/dashboard" replace />} />
          <Route path="/employer/marketplace" element={user ? <TalentDashboard /> : <Navigate to="/employer/login" replace />} />
          <Route path="/enterprise" element={user ? <EnterpriseDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/ruta-al-exito" element={user ? <PathToSuccessPage /> : <Navigate to="/login" replace />} />
          <Route path="/perfil" element={user ? <ProfilePage /> : <Navigate to="/login" replace />} />
          <Route path="/training" element={user ? <TrainingDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/training/azure-admin-module-1" element={user ? <AzureAdminModule1 /> : <Navigate to="/login" replace />} />
          <Route path="/training/az-900" element={user ? <AzureFundamentals /> : <Navigate to="/login" replace />} />
          <Route path="/training/az-104" element={user ? <AzureAdministrator /> : <Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isProtectedRoute && <Footer />}
      {/* Show feedback button when user is logged in and not on homepage */}
      {user && !isHomePage && !isAdmin && <FeedbackButton />}
      {/* Show floating chatbot only on homepage */}
      {isHomePage && <FloatingChatbot />}
    </>
  );
}

export default App;